import type { ConnectionScope } from "@typedoc/contracts/connections";
import { Switch } from "@typedoc/ui";

/**
 * Whether people in the workspace may use this account in their chats. It sits at the top of the
 * Permissions card, above Look only, because the rules below only matter once chats may use the
 * account at all. A personal account has no row: its owner's chats use it anyway. Unlike the
 * decisions below it saves the moment it is flipped, so while the save is in flight the switch
 * holds still and shows the value being saved. It moves the account's revision on, which the
 * details page follows in a pending Permissions draft so that Save is not refused for it. The
 * `use-in-chat` id is for links that scroll here. While the Permissions save is in flight it holds
 * still as well (`otherSaving`): both write against the same revision, so one would be refused for
 * the other.
 */
export function UseInChatRow({
  scope,
  checked,
  canManage,
  saving,
  otherSaving,
  onChange,
}: {
  scope: ConnectionScope;
  checked: boolean;
  canManage: boolean;
  saving: boolean;
  /** The Permissions draft is being saved. */
  otherSaving: boolean;
  onChange: (value: boolean) => void;
}) {
  if (scope !== "workspace") return null;
  return (
    <div id="use-in-chat" className="flex items-start gap-3 border-b border-border px-5 py-4">
      <div className="min-w-0 flex-1">
        <p id="use-in-chat-title" className="text-[13px]/5 font-medium">
          Use in chat
        </p>
        <p className="text-xs/[18px] text-muted-foreground">
          Lets people in this workspace use this account in their chats, within the rules below.
          {canManage ? (
            ""
          ) : (
            <span id="use-in-chat-reason"> A workspace manager can change this.</span>
          )}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={!canManage || saving || otherSaving}
        onCheckedChange={onChange}
        aria-labelledby="use-in-chat-title"
        aria-describedby={canManage ? undefined : "use-in-chat-reason"}
        className="mt-0.5"
      />
    </div>
  );
}
